import { useState, useEffect } from 'react';
import { collection, doc, getDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { frozenTermsFields } from '../rentalTerms';
import { T, tone } from '../theme';
import { ActionBtn, Badge, BikeThumb, ConfirmModal, EmptyState, FilterBar, Icon, PhotoGrid, SearchBar, SectionHeader, ShowMore, StickyBar, SummaryRow, useBikeThumbs, usePaged } from '../ui';
import { BOOKING_STATUS_LABEL, BOOKING_TERMINAL_STATUSES, cancelReasonLabel, downloadCsv, toCsv } from '../lib/format';
import { UserLink } from '../profile';

/**
 * A rental that never ended.
 *
 * `ongoing` is where a return stalls now: return-photos.tsx writes
 * `completed` directly, so when the owner never opens it the booking stays
 * ongoing for good, holding the bike off the platform.
 *
 * DELIBERATELY NOT SWEPT BY THE SERVER. functions/stuckSteps.js moves stuck
 * photo steps on by itself, and `ongoing` is excluded from it, because this
 * is also exactly what a renter who is two days late looks like — nothing on
 * the booking tells the two apart. Closing it from a clock would free a bike
 * somebody is still riding and freeze a late fee the owner is owed. So it
 * comes here instead, for a person who can ring and ask.
 *
 * Force Complete on the booking itself is the action; this only finds them.
 */
const OVERDUE_HOURS = 24;

function OverdueRentals({ bookings, isMobile, onOpen }) {
  const rows = [];
  for (const b of bookings) {
    if (b.status !== 'ongoing' || !b.endDate || !b.endTime) continue;
    // Maldives is UTC+5, as everywhere else that reads these fields.
    const end = new Date(`${b.endDate} ${b.endTime} GMT+0500`).getTime();
    if (!Number.isFinite(end)) continue;
    const hours = (Date.now() - end) / 3600000;
    if (hours < OVERDUE_HOURS) continue;
    rows.push({ ...b, hours, days: Math.floor(hours / 24) });
  }
  if (!rows.length) return null;
  rows.sort((a, b) => b.hours - a.hours);

  return (
    <div style={{
      backgroundColor: T.card, border: `1px solid ${tone('danger').border}`,
      borderRadius: 14, padding: isMobile ? 14 : 18, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ color: T.textPrimary, fontSize: 14, fontWeight: 700 }}>
          Rentals that never ended
        </div>
        <div style={{ color: T.textMuted, fontSize: 11.5 }}>
          {rows.length} over {OVERDUE_HOURS}h past return
        </div>
      </div>
      <div style={{ color: T.textMuted, fontSize: 12, lineHeight: 1.6, margin: '8px 0 12px' }}>
        Still <strong>ongoing</strong>, so the bike is held. Usually the owner never added the
        return photos — but it looks identical to a renter who still has the bike, which is why
        nothing closes these automatically. Ring, then use <strong>Force Complete</strong> on the
        booking.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rows.slice(0, 15).map(b => (
          <div
            key={b.id}
            onClick={() => onOpen && onOpen(b.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              padding: '9px 0', borderBottom: `1px solid ${T.cardBorder}`,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: T.textPrimary, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {b.bikeName || 'Bike'}
                <span style={{ color: T.textMuted, fontWeight: 500 }}> · {b.bookingRef || b.id.slice(0, 6)}</span>
              </div>
              <div style={{ color: T.textMuted, fontSize: 11.5, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {b.renterName || 'Renter'}
                {b.renterPhone ? ` +960 ${b.renterPhone}` : ''} · owner {b.ownerName || '—'}
                {b.ownerPhone ? ` +960 ${b.ownerPhone}` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ color: tone('danger').color, fontSize: 12, fontWeight: 700 }}>
                {b.days >= 1 ? `${b.days}d late` : `${Math.floor(b.hours)}h late`}
              </div>
              <div style={{ color: T.textMuted, fontSize: 11 }}>due {b.endDate}</div>
            </div>
          </div>
        ))}
        {rows.length > 15 ? (
          <div style={{ color: T.textMuted, fontSize: 11.5, marginTop: 10 }}>
            and {rows.length - 15} more — filter to Active to see them all.
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function Bookings({ isMobile }) {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setBookings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => { console.error('Bookings error:', error); setLoading(false); });
    return () => unsub();
  }, []);

  const activeStatuses = ['pending_owner_approval', 'pending_payment', 'pending_pickup_photos', 'pending_pickup_approval', 'ongoing', 'pending_return_photos', 'pending_return_confirmation'];

  const counts = {
    all: bookings.length,
    active: bookings.filter(b => activeStatuses.includes(b.status)).length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
  };
  const searchQ = search.trim().toLowerCase();
  const filtered = bookings
    .filter(b => {
      if (filter === 'all') return true;
      if (filter === 'active') return activeStatuses.includes(b.status);
      return b.status === filter;
    })
    .filter(b => !searchQ ||
      b.bookingRef?.toLowerCase().includes(searchQ) ||
      b.bikeName?.toLowerCase().includes(searchQ) ||
      b.renterName?.toLowerCase().includes(searchQ) ||
      b.ownerName?.toLowerCase().includes(searchQ)
    );

  const paged = usePaged(filtered, `${filter}|${searchQ}`);
  // Only the rows actually on screen are resolved, so the whole booking
  // history is never looked up just to draw the first page.
  const thumbs = useBikeThumbs([...new Set(paged.visible.map(b => b.bikeId).filter(Boolean))].join(','));

  const statusColor = (status) => {
    if (status === 'completed') return { color: T.success, bg: T.successDim, border: T.successBorder };
    if (status === 'cancelled') return { color: T.danger, bg: T.dangerDim, border: T.dangerBorder };
    if (status === 'ongoing') return { color: T.primary, bg: T.primaryDim, border: T.primaryBorder };
    return { color: T.warning, bg: T.warningDim, border: T.warningBorder };
  };

  const exportCsv = () => {
    const csv = toCsv(filtered, [
      { label: 'Booking ID', value: r => r.bookingRef },
      { label: 'Bike', value: r => r.bikeName },
      { label: 'Renter', value: r => r.renterName },
      { label: 'Owner', value: r => r.ownerName },
      { label: 'Start Date', value: r => r.startDate },
      { label: 'End Date', value: r => r.endDate },
      { label: 'Total (MVR)', value: r => r.total },
      { label: 'Status', value: r => r.status },
      { label: 'Late Fee (MVR)', value: r => r.lateFee?.amount },
      { label: 'Cancel Reason', value: r => cancelReasonLabel(r.cancelReason) || '' },
    ]);
    downloadCsv(`saikapp-bookings-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  if (loading) return <div style={{ color: T.textMuted, padding: 40 }}>Loading bookings...</div>;

  if (selectedId) {
    return <BookingThread bookingId={selectedId} isMobile={isMobile} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div>
      <SectionHeader title="Bookings" subtitle="All rental bookings and their current status — click one for full detail" />
      <OverdueRentals bookings={bookings} isMobile={isMobile} onOpen={setSelectedId} />
      <SearchBar value={search} onChange={setSearch} placeholder="Search by booking ID, bike, renter, or owner..." />
      <StickyBar>
        <FilterBar options={['all', 'active', 'completed', 'cancelled']} value={filter} onChange={setFilter} counts={counts} bare />
        <ActionBtn label="Export CSV" icon="download" onClick={exportCsv} variant="approve" />
      </StickyBar>

      {filtered.length === 0 ? <EmptyState message="No bookings found" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {paged.visible.map(b => {
            const sc = statusColor(b.status);
            return (
              <div
                key={b.id}
                className="ui-row"
                onClick={() => setSelectedId(b.id)}
                style={{
                  backgroundColor: T.card, border: `1px solid ${T.cardBorder}`, cursor: 'pointer',
                  borderRadius: 14, padding: isMobile ? 16 : 18,
                  display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 12 : 16, alignItems: isMobile ? 'stretch' : 'center',
                }}
              >
                <div style={{ display: 'flex', gap: 13, flex: 1, minWidth: 0, alignItems: 'center' }}>
                <BikeThumb src={thumbs.get(b.bikeId) || ''} size={isMobile ? 48 : 58} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {b.bookingRef && (
                    <div style={{ color: T.textMuted, fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3, marginBottom: 3 }}>{b.bookingRef}</div>
                  )}
                  <div style={{ color: T.textPrimary, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{b.bikeName || 'Bike'}</div>
                  <div style={{ color: T.textMuted, fontSize: 13 }}>Renter: <UserLink userId={b.renterId} name={b.renterName} fallback={b.renterId} color={T.textSecondary} /></div>
                  <div style={{ color: T.textMuted, fontSize: 13 }}>Owner: <UserLink userId={b.ownerId} name={b.ownerName} color={T.owner} /></div>
                  {/* Why it was cancelled, on the row itself. Having to open
                      each one to find out made the list unreadable. */}
                  {b.status === 'cancelled' && (
                    <div style={{ color: T.danger, fontSize: 12.5, marginTop: 4 }}>
                      {cancelReasonLabel(b.cancelReason) || 'Reason not recorded'}
                    </div>
                  )}
                  {(b.lateFee || b.pendingExtension || (b.status === 'ongoing' && !b.rentalAgreement)) && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {b.lateFee && (
                        <Badge
                          label={`Late fee MVR ${b.lateFee.amount} ${b.lateFee.status === 'paid' ? '· Paid' : '· Pending'}`}
                          color={b.lateFee.status === 'paid' ? T.success : T.warning}
                          bg={b.lateFee.status === 'paid' ? T.successDim : T.warningDim}
                          border={b.lateFee.status === 'paid' ? T.successBorder : T.warningBorder}
                        />
                      )}
                      {b.pendingExtension && (
                        <Badge
                          label={b.pendingExtension.status === 'confirmed' ? 'Extended' : `Extension pending → ${b.pendingExtension.newEndDate}`}
                          color={b.pendingExtension.status === 'confirmed' ? T.success : T.warning}
                          bg={b.pendingExtension.status === 'confirmed' ? T.successDim : T.warningDim}
                          border={b.pendingExtension.status === 'confirmed' ? T.successBorder : T.warningBorder}
                        />
                      )}
                      {b.status === 'ongoing' && !b.rentalAgreement && (
                        <Badge label="No rental agreement" color={T.danger} bg={T.dangerDim} border={T.dangerBorder} />
                      )}
                    </div>
                  )}
                </div>
                </div>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: T.textMuted, fontSize: 12, marginBottom: 2 }}>Period</div>
                    <div style={{ color: T.textSecondary, fontSize: 13 }}>{b.startDate} → {b.endDate}</div>
                  </div>
                  <div>
                    <div style={{ color: T.textMuted, fontSize: 12, marginBottom: 2 }}>Total</div>
                    <div style={{ color: T.primary, fontSize: 15, fontWeight: 700 }}>MVR {b.total}</div>
                  </div>
                  <Badge label={b.status?.replace(/_/g, ' ')} color={sc.color} bg={sc.bg} border={sc.border} />
                  <Icon name="chevronRight" size={16} color={T.textMuted} />
                </div>
              </div>
            );
          })}
          <ShowMore remaining={paged.remaining} onClick={paged.more} />
        </div>
      )}
    </div>
  );
}

// Booking detail / dispute view — mirrors ReportThread's list/detail flip
// pattern, but read-only: shows pickup/return photos, lateFee/extension/
// agreement data, and the full booking chat log (payment slips, extension
// requests, etc.) so a dispute can be resolved without touching Firestore
// directly.

export function BookingChatMessage({ m }) {
  const isAdmin = m.sender === 'admin';
  const isRenter = m.sender === 'renter';
  const align = isAdmin ? 'flex-end' : 'flex-start';
  const bubbleBg = isAdmin ? T.primary : T.cardInner;
  const bubbleBorder = isAdmin ? 'none' : `1px solid ${T.cardBorder}`;
  const textColor = isAdmin ? '#fff' : T.textPrimary;
  const senderLabel = isAdmin ? null : (m.senderName || (isRenter ? 'Renter' : m.sender === 'system_pickup' || m.sender === 'system_return' ? 'System' : 'Owner'));

  const timeLabel = m.createdAt?.toDate
    ? m.createdAt.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';

  const body = (() => {
    switch (m.type) {
      case 'image':
        return <img src={m.image} alt="attachment" style={{ maxWidth: 220, borderRadius: 8, display: 'block' }} />;
      case 'pickup_photos':
      case 'return_photos':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {m.text && <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{m.text}</div>}
            <PhotoGrid photos={m.photos} />
          </div>
        );
      case 'payment_details':
        return (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Payment Details</div>
            {m.bankName && <div>Bank: {m.bankName}</div>}
            {m.accountName && <div>Account: {m.accountName}</div>}
            {m.accountNumber && <div>Number: {m.accountNumber}</div>}
            {m.amount && <div>Amount: MVR {m.amount}</div>}
            {m.contactName && <div>Contact: {m.contactName}{m.contactPhone ? ` · ${m.contactPhone}` : ''}</div>}
          </div>
        );
      case 'delivery_info':
        return (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Delivery Details</div>
            {m.pickupAddress && <div>Pickup: {m.pickupAddress}</div>}
            {m.dropoffAddress && <div>Drop-off: {m.dropoffAddress}</div>}
          </div>
        );
      case 'booking_confirmed':
        return (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Booking Confirmed</div>
            {m.contactName && <div>Contact: {m.contactName}{m.contactPhone ? ` · ${m.contactPhone}` : ''}</div>}
            {m.pickupLocation && <div>Pickup: {m.pickupLocation}</div>}
          </div>
        );
      case 'late_fee_notice':
        return (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Late Fee Notice</div>
            <div>{m.hours}h late · MVR {m.ratePerHour}/hr · Total MVR {m.amount}</div>
          </div>
        );
      case 'extension_request':
        return (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Extension Requested</div>
            <div>{m.oldEndDate} {m.oldEndTime} → {m.newEndDate} {m.newEndTime}</div>
            <div>{m.billingLabel || `MVR ${m.amount}`}</div>
          </div>
        );
      default:
        return <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{m.text}</div>;
    }
  })();

  return (
    <div style={{ display: 'flex', justifyContent: align }}>
      <div style={{ maxWidth: '75%', borderRadius: 12, padding: '10px 14px', backgroundColor: bubbleBg, border: bubbleBorder }}>
        {senderLabel && <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 3 }}>{senderLabel}</div>}
        <div style={{ color: textColor }}>{body}</div>
        {timeLabel && (
          <div style={{ color: isAdmin ? '#ffffff99' : T.textMuted, fontSize: 10.5, marginTop: 4, textAlign: 'right' }}>{timeLabel}</div>
        )}
      </div>
    </div>
  );
}

/**
 * The same thread, opened from anywhere else in the panel.
 *
 * It shares BookingThread rather than reimplementing it, so an admin action
 * taken from a person's history behaves exactly as it does in the Bookings
 * tab. Escape closes it, and so does the backdrop.
 */
export function BookingModal({ bookingId, onClose }) {
  useEffect(() => {
    if (!bookingId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bookingId, onClose]);

  if (!bookingId) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(9, 12, 18, 0.62)',
        backdropFilter: 'blur(3px)', zIndex: 400,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 20, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: T.bg, border: `1px solid ${T.cardBorder}`, borderRadius: 16,
          width: '100%', maxWidth: 900, padding: 20, margin: 'auto 0',
          boxShadow: T.shadowLg,
        }}
      >
        <BookingThread bookingId={bookingId} isMobile={false} onBack={onClose} />
      </div>
    </div>
  );
}

export function BookingThread({ bookingId, isMobile, onBack }) {
  const [booking, setBooking] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null); // 'cancel' | 'advance-complete' | 'advance-ongoing' | null
  const [acting, setActing] = useState(false);
  const [advanceError, setAdvanceError] = useState('');
  const thumbs = useBikeThumbs(booking?.bikeId || '');

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'bookings', bookingId), snap => {
      setBooking(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    });
    return () => unsub();
  }, [bookingId]);

  useEffect(() => {
    const q = query(collection(db, 'chats', bookingId, 'messages'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => setMessages([]));
    return () => unsub();
  }, [bookingId]);

  const forceCancel = async (reason) => {
    setActing(true);
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'cancelled', cancelReason: 'admin_override',
        cancelledAt: serverTimestamp(), adminCancelNote: reason,
      });
    } catch (e) { console.error(e); }
    finally { setActing(false); setActionModal(null); }
  };

  // Completing from pending_return_confirmation mirrors the client's own
  // 1-hour auto-progression exactly (bare status flip) —
  // app/(renter)/bookings.tsx:558-568.
  //
  // From ongoing or pending_return_photos it is a genuine override: the bike
  // came back but the owner never submitted return photos, so the booking has
  // no photo record of its condition at return. That is recorded on the
  // document rather than left to look like a normal completion — a dispute
  // later needs to be able to tell the difference.
  const forceAdvanceToCompleted = async (reason) => {
    setActing(true);
    try {
      const update = { status: 'completed' };
      if (!booking.returnPhotos) {
        update.completedBy = 'admin_override';
        update.adminCompleteNote = reason || '';
        update.completedAt = serverTimestamp();
      }
      await updateDoc(doc(db, 'bookings', bookingId), update);
    } catch (e) { console.error(e); }
    finally { setActing(false); setActionModal(null); }
  };

  // Mirrors the rentalAgreement snapshot construction duplicated in
  // app/pickup-photos-review.tsx:93-109 and app/(owner)/bookings.tsx:617-633
  // (the two client-side paths that normally generate it) — keep this in
  // sync with those if the agreement shape ever changes.
  const forceAdvanceToOngoing = async () => {
    setAdvanceError('');
    if (!booking.pickupPhotos) {
      setAdvanceError('No pickup photos on this booking — the renter has not confirmed pickup, so advancing to Ongoing would be wrong. Use Force Cancel instead if this booking is stuck.');
      return;
    }
    if (!booking.bikeId) {
      setAdvanceError('This booking has no bikeId — cannot look up the bike to build the rental agreement.');
      return;
    }
    setActing(true);
    try {
      const bikeSnap = await getDoc(doc(db, 'bikes', booking.bikeId));
      if (!bikeSnap.exists()) {
        setAdvanceError('The bike for this booking no longer exists.');
        setActing(false);
        return;
      }
      const bike = bikeSnap.data();
      const durationHours = Number(booking.durationHours) || 24;
      const endDateTime = new Date(Date.now() + durationHours * 60 * 60 * 1000);
      const endDateStr = endDateTime.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' });
      const endTimeStr = endDateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

      const rentalAgreement = {
        generatedAt: new Date().toISOString(),
        renterName: booking.renterName || '',
        renterPhone: booking.renterPhone || '',
        renterNid: booking.renterNid || '',
        ownerName: booking.ownerName || '',
        ownerPhone: booking.ownerPhone || '',
        ownerNid: booking.ownerNid || '',
        bikeModel: booking.bikeName || '',
        plate: bike.plate || '',
        cc: bike.cc || '',
        year: bike.year || '',
        startDate: booking.startDate || '',
        startTime: booking.startTime || '',
        endDate: endDateStr,
        endTime: endTimeStr,
        total: booking.total,
        lateFeePerHour: Number(bike.lateFeePerHour) || 0,
        ...frozenTermsFields(),
      };

      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'ongoing', rentalStartedAt: serverTimestamp(),
        endDate: endDateStr, endTime: endTimeStr, rentalAgreement,
      });
    } catch (e) { console.error(e); setAdvanceError('Something went wrong — check the console.'); }
    finally { setActing(false); setActionModal(null); }
  };

  if (loading) return <div style={{ color: T.textMuted, padding: 40 }}>Loading booking...</div>;
  if (!booking) return <EmptyState message="Booking not found" />;

  const isTerminal = BOOKING_TERMINAL_STATUSES.includes(booking.status);
  // Whether the bike was ever actually handed over. Completing before this
  // point records a rental that did not happen, which is a different and
  // stronger claim than completing one that merely lacks return photos.
  const rentalStarted = ['ongoing', 'pending_return_photos', 'pending_return_confirmation']
    .includes(booking.status);

  const sc = (() => {
    if (booking.status === 'completed') return { color: T.success, bg: T.successDim, border: T.successBorder };
    if (booking.status === 'cancelled') return { color: T.danger, bg: T.dangerDim, border: T.dangerBorder };
    if (booking.status === 'ongoing') return { color: T.primary, bg: T.primaryDim, border: T.primaryBorder };
    return { color: T.warning, bg: T.warningDim, border: T.warningBorder };
  })();

  return (
    <div>
      <button onClick={onBack} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
        color: T.textMuted, cursor: 'pointer', fontSize: 13, fontWeight: 700, marginBottom: 16, padding: 0,
      }}>
        <Icon name="chevronLeft" size={15} color={T.textMuted} /> Back to Bookings
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', minWidth: 0 }}>
          <BikeThumb src={thumbs.get(booking.bikeId) || ''} size={62} radius={11} />
        <div>
          <h1 style={{ color: T.textPrimary, fontSize: 22, fontWeight: 700, margin: 0 }}>{booking.bikeName || 'Booking'}</h1>
          <div style={{ color: T.textMuted, fontSize: 13, marginTop: 4 }}>
            {booking.bookingRef} ·{' '}
            <UserLink userId={booking.renterId} name={booking.renterName} color={T.textSecondary} size={13} />
            {' ↔ '}
            <UserLink userId={booking.ownerId} name={booking.ownerName} color={T.owner} size={13} />
          </div>
        </div>
        </div>
        <Badge label={BOOKING_STATUS_LABEL[booking.status] || booking.status} color={sc.color} bg={sc.bg} border={sc.border} />
      </div>

      {/* Summary */}
      <div style={{
        backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`, borderRadius: 14,
        padding: 16, marginBottom: 16,
      }}>
        <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.3, marginBottom: 4 }}>SUMMARY</div>
        <SummaryRow label="Period" value={`${booking.startDate} ${booking.startTime} → ${booking.endDate} ${booking.endTime}`} />
        <SummaryRow label="Total" value={`MVR ${booking.total}`} />
        {booking.lateFee && (
          <SummaryRow label="Late Fee" value={`MVR ${booking.lateFee.amount} (${booking.lateFee.hours}h) · ${booking.lateFee.status === 'paid' ? 'Paid' : 'Pending'}`} />
        )}
        {booking.pendingExtension && (
          <SummaryRow label="Extension" value={`${booking.pendingExtension.newEndDate} ${booking.pendingExtension.newEndTime} · MVR ${booking.pendingExtension.amount} · ${booking.pendingExtension.status}`} />
        )}
        {booking.status === 'cancelled' && (
          <SummaryRow
            label="Cancel Reason"
            value={cancelReasonLabel(booking.cancelReason)
              || 'Not recorded — cancelled before the app started saving a reason'}
          />
        )}
        {booking.cancelReasonInferred && (
          <SummaryRow label="" value="Worked out from the booking's state, not stated by whoever cancelled" />
        )}
        {booking.adminCancelNote && <SummaryRow label="Admin Note" value={booking.adminCancelNote} />}
        {booking.completedBy === 'admin_override' && (
          <SummaryRow label="Completed By" value={`Admin override${booking.adminCompleteNote ? ` — ${booking.adminCompleteNote}` : ''}`} />
        )}
        <SummaryRow label="Rental Agreement" value={booking.rentalAgreement ? 'Generated' : (booking.status === 'ongoing' || booking.status === 'completed' ? 'Missing' : '—')} />
      </div>

      {/* Admin overrides — for bookings genuinely stuck (neither party's app */}
      {/* progressed the status). Regular users never see these controls. */}
      {!isTerminal && (
        <div style={{
          backgroundColor: T.cardInner, border: `1px solid ${T.dangerBorder}`, borderRadius: 14,
          padding: 16, marginBottom: 16,
        }}>
          <div style={{ color: T.danger, fontSize: 11, fontWeight: 700, letterSpacing: 0.3, marginBottom: 10 }}>ADMIN OVERRIDE</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {booking.status === 'pending_pickup_approval' && (
              <ActionBtn label="Force Advance to Ongoing" icon="check" variant="warning" onClick={() => { setAdvanceError(''); setActionModal('advance-ongoing'); }} />
            )}
            {/* Offered on every active booking. The status-specific judgement
                lives in the confirmation copy instead of in what is clickable —
                admin needs the escape hatch to exist everywhere, but should be
                told plainly what completing this particular booking asserts. */}
            <ActionBtn label="Force Complete" icon="check" variant="warning" onClick={() => setActionModal('advance-complete')} />
            <ActionBtn label="Force Cancel" icon="x" variant="reject" onClick={() => setActionModal('cancel')} />
          </div>
        </div>
      )}

      {/* Pickup / return photos */}
      {(booking.pickupPhotos || booking.returnPhotos) && (
        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16,
          backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`, borderRadius: 14,
          padding: 16, marginBottom: 16,
        }}>
          {booking.pickupPhotos && (
            <div style={{ flex: 1 }}>
              <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.3, marginBottom: 8 }}>PICKUP PHOTOS</div>
              <PhotoGrid photos={booking.pickupPhotos} />
            </div>
          )}
          {booking.returnPhotos && (
            <div style={{ flex: 1 }}>
              <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.3, marginBottom: 8 }}>RETURN PHOTOS</div>
              <PhotoGrid photos={booking.returnPhotos} />
            </div>
          )}
        </div>
      )}

      {/* Chat log */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 12,
        backgroundColor: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14,
        padding: 16, marginBottom: 16, minHeight: 120,
      }}>
        <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>CONVERSATION</div>
        {messages.length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>No messages yet.</div>
        ) : messages.map(m => <BookingChatMessage key={m.id} m={m} />)}
      </div>

      <ConfirmModal
        open={actionModal === 'cancel'}
        title="Force cancel this booking?"
        message="This cancels the booking outright and notifies both the renter and owner. Only do this for a booking that's genuinely stuck or invalid — it does not un-hand-over a bike that's already been picked up."
        requireReason
        confirmLabel={acting ? 'Cancelling...' : 'Force Cancel'}
        danger
        onCancel={() => setActionModal(null)}
        onConfirm={forceCancel}
      />

      <ConfirmModal
        open={actionModal === 'advance-complete'}
        title="Force complete this booking?"
        message={booking.returnPhotos
          ? "Return photos are on file. This just marks the booking completed, the same as the app does on its own. Both parties can then rate each other."
          : rentalStarted
          ? "There are NO return photos on this booking, so completing it closes the rental with no record of the bike's condition at return. Only do this if you have confirmed with the owner that the bike is genuinely back. The override is recorded on the booking."
          : "This booking never reached pickup — the bike was never handed over. Completing it records a rental that did not happen, and both parties will be asked to rate each other for it. Force Cancel is almost certainly what you want instead. The override is recorded on the booking."}
        requireReason={!booking.returnPhotos}
        confirmLabel={acting ? 'Completing...' : 'Force Complete'}
        onCancel={() => setActionModal(null)}
        onConfirm={forceAdvanceToCompleted}
      />

      <ConfirmModal
        open={actionModal === 'advance-ongoing'}
        title="Force advance to Ongoing?"
        message={advanceError || "This starts the rental now and generates the rental agreement (the renter's app didn't do this after the owner approved pickup). Only use this if the renter has genuinely already picked up the bike."}
        confirmLabel={acting ? 'Starting...' : 'Force Advance'}
        onCancel={() => setActionModal(null)}
        onConfirm={() => !advanceError && forceAdvanceToOngoing()}
      />
    </div>
  );
}

// ── Reports ──────────────────────────────────────────────
